import { lazy, Suspense, useEffect, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";

import { Landing } from "./Landing.js";
import { Lobby } from "./Lobby.js";
import { CommandNotice } from "./CommandNotice.js";
import { loadMatchScreen } from "./matchScreenLoader.js";
import { useClientStore } from "./store.js";
import {
  getAnalyticsConsent,
  recordUsableSurface,
  subscribeAnalyticsConsent,
} from "./analytics.js";

const MatchScreen = lazy(async () => {
  const module = await loadMatchScreen();
  return { default: module.MatchScreen };
});

export function App() {
  const { t } = useTranslation();
  const lobby = useClientStore((state) => state.lobby);
  const view = useClientStore((state) => state.view);
  const notice = useClientStore((state) => state.notice);
  const clearNotice = useClientStore((state) => state.clearNotice);
  const connected = useClientStore((state) => state.connected);
  const session = useClientStore((state) => state.session);
  const analyticsConsent = useSyncExternalStore(
    subscribeAnalyticsConsent,
    getAnalyticsConsent,
    getAnalyticsConsent,
  );
  const connecting = !connected && lobby === null && view === null && notice?.kind !== "error";

  useEffect(() => {
    if (notice === null || notice.kind === "error") return;
    const timer = window.setTimeout(clearNotice, 3_200);
    return () => window.clearTimeout(timer);
  }, [clearNotice, notice]);

  useEffect(() => {
    if (connecting) return;
    const landingVisible = view === null && lobby === null;
    const hasPersistedSession = localStorage.getItem("pactfront.session.v1") !== null;
    if (landingVisible && session === null && hasPersistedSession) return;
    recordUsableSurface(landingVisible ? "landing" : "resume", landingVisible);
  }, [analyticsConsent, connecting, lobby, session, view]);

  return (
    <>
      {connecting ? (
        <main className="loading-state" aria-busy="true">
          <div className="wordmark">{t("app.brand")}</div>
          <p>{t("common.loading")}</p>
          <button type="button" onClick={() => window.location.reload()}>
            {t("common.retry")}
          </button>
        </main>
      ) : view !== null ? (
        <Suspense
          fallback={
            <main className="loading-state" aria-busy="true">
              <div className="wordmark">{t("app.brand")}</div>
              <p>{t("common.loading")}</p>
            </main>
          }
        >
          <MatchScreen />
        </Suspense>
      ) : lobby !== null ? (
        <Lobby />
      ) : (
        <Landing />
      )}
      {notice !== null && view === null ? (
        <div className="standalone-notification">
          <CommandNotice notice={notice} onDismiss={clearNotice} />
        </div>
      ) : null}
      {!connected ? (
        <div className="connection-banner" role="status" aria-live="polite">
          {t("common.reconnecting")}
        </div>
      ) : null}
    </>
  );
}
